import React, { CSSProperties } from "react";

interface ServiceItem {
  name: string;
  description?: string;
  credit?: string;
  baht?: string;
  note?: string;
}

interface PricingCardExportProps {
  planName?: string;
  credit?: string;
  price?: string;
  unit?: string;
  serviceLabel?: string;
  serviceCreditLabel?: string;
  bahtLabel?: string;
  services?: ServiceItem[];
  features?: string[];
  note?: string;
  badgeColor?: string;
  headerBackground?: string;
  bahtHeaderBg?: string;
  checkColor?: string;
  headerTextColor?: string;
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: number;
  showShadow?: boolean;
  textFontFamily?: string;
}

export const PricingCardExport: React.FC<PricingCardExportProps> = ({
  planName = "\u0E41\u0E1E\u0E47\u0E01\u0E40\u0E25\u0E47\u0E01\u0E2A\u0E38\u0E14\u0E04\u0E38\u0E49\u0E21",
  credit = "600",
  price = "300",
  unit = "\u0E3F/\u0E04\u0E23\u0E31\u0E49\u0E07",
  serviceLabel = "Service",
  serviceCreditLabel = "Credit",
  bahtLabel = "Baht",
  services = [],
  features,
  note,
  badgeColor = "linear-gradient(180deg, #F2819F 0%, #FAAF40 100%)",
  headerBackground = "linear-gradient(116.39deg, #FFEBCF 2.25%, #EFFFF3 55.82%, #FFF7EE 100%)",
  bahtHeaderBg = "linear-gradient(90deg, #FFECCF 0%, #DAFFDC 100%)",
  checkColor = "#00B97E",
  headerTextColor = "#2C2C2B",
  backgroundColor = "#ffffff",
  textColor = "#2C2C2B",
  borderRadius = 20,
  showShadow = true,
  textFontFamily,
}) => {
  const hasThai = (s: string) => /[\u0E00-\u0E7F]/.test(s);
  const ffStyle = (text?: string): CSSProperties => textFontFamily ? { fontFamily: `'${textFontFamily}', var(--font-family), sans-serif` } : (text && !hasThai(text)) ? { fontFamily: "var(--font-family-en, var(--font-family))" } : {};

  // Legacy: convert old features[] to services[]
  const resolvedServices: ServiceItem[] =
    services && services.length > 0
      ? services
      : (Array.isArray(features) ? features : []).map((f) => ({ name: f }));

  // Split unit: "฿/ครั้ง" → currency "฿" (large) + suffix "/ครั้ง" (small)
  const slashIdx = unit.indexOf("/");
  const currencyPart = slashIdx > 0 ? ` ${unit.substring(0, slashIdx)}` : "";
  const unitSuffix = slashIdx > 0 ? unit.substring(slashIdx) : unit;

  const checkSvg = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={checkColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  const cardStyles: CSSProperties = {
    position: "relative",
    borderRadius: `${borderRadius}px`,
    overflow: "hidden",
    backgroundColor,
    boxShadow: showShadow ? "0px 0px 20px 5px rgba(0, 0, 0, 0.1)" : "none",
    width: "100%",
    maxWidth: "320px",
  };

  return (
    <div style={cardStyles} className="pricing-card" data-element="pricing-item" data-type="content">
      {/* ─── Header gradient area (inset) ─── */}
      <div
        style={{
          margin: "14px 14px 0",
          background: headerBackground,
          borderRadius: `${borderRadius}px`,
          padding: "10px 16px 14px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: "90px",
        }}
      >
        {/* Plan name badge — white pill */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            borderRadius: "20px",
            padding: "2px 10px",
            alignSelf: "flex-start",
          }}
        >
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              background: badgeColor,
              flexShrink: 0,
              display: "inline-block",
            }}
          />
          <span
            style={{
              ...ffStyle(planName),
              fontSize: "0.875rem",
              fontWeight: 300,
              color: "#000000",
              lineHeight: 1.4,
            }}
          >
            {planName}
          </span>
        </div>

        {/* Credits — bottom right */}
        <div
          style={{
            alignSelf: "flex-end",
            display: "flex",
            alignItems: "baseline",
            gap: "4px",
            marginTop: "4px",
          }}
        >
          <span
            style={{
              fontFamily: "'Fahkwang', var(--font-family), sans-serif",
              fontSize: "2rem",
              fontWeight: 400,
              color: headerTextColor,
              lineHeight: 1,
            }}
          >
            {credit}
          </span>
          <span
            style={{
              fontFamily: "'Fahkwang', var(--font-family), sans-serif",
              fontSize: "1rem",
              fontWeight: 400,
              color: headerTextColor,
              lineHeight: 1,
            }}
          >
            Credits
          </span>
        </div>
      </div>

      {/* ─── Price section ─── */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          padding: "14px 30px 10px",
        }}
      >
        <span
          style={{
            ...ffStyle("\u0E23\u0E32\u0E04\u0E32"),
            fontSize: "1.125rem",
            fontWeight: 400,
            color: textColor,
          }}
        >
          {"\u0E23\u0E32\u0E04\u0E32"}
        </span>
        <div style={{ display: "flex", alignItems: "baseline" }}>
          <span
            style={{
              ...ffStyle(price + currencyPart),
              fontSize: "2.25rem",
              fontWeight: 500,
              color: textColor,
              lineHeight: 1.2,
            }}
          >
            {price}{currencyPart}
          </span>
          <span
            style={{
              ...ffStyle(unitSuffix),
              fontSize: "1.125rem",
              fontWeight: 400,
              color: textColor,
              marginLeft: "4px",
            }}
          >
            {unitSuffix}
          </span>
        </div>
      </div>

      {/* ─── Service Table ─── */}
      {resolvedServices.length > 0 && (
        <div style={{ paddingBottom: "16px" }}>
          {/* Table Header — dark pill + gradient pill */}
          <div style={{ display: "flex", margin: "0 16px", gap: "6px", marginBottom: "4px" }}>
            {/* Dark pill: Service + Credit */}
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                backgroundColor: "#404040",
                borderRadius: "30px",
                height: "24px",
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  flex: 1,
                  paddingLeft: "12px",
                  ...ffStyle(serviceLabel),
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  color: "#ffffff",
                }}
              >
                {serviceLabel}
              </span>
              <span
                style={{
                  width: "55px",
                  textAlign: "center",
                  ...ffStyle(serviceCreditLabel),
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  color: "#ffffff",
                }}
              >
                {serviceCreditLabel}
              </span>
            </div>
            {/* Gradient pill: Baht */}
            <div
              style={{
                width: "65px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: bahtHeaderBg,
                borderRadius: "30px",
                height: "24px",
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  ...ffStyle(bahtLabel),
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  color: textColor,
                }}
              >
                {bahtLabel}
              </span>
            </div>
          </div>

          {/* Table Rows */}
          {resolvedServices.map((service, index) => {
            const hasValues = !!(service.credit || service.baht);
            const isLongText = !service.credit && service.baht && service.baht.length > 6;

            return (
              <div key={index}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "3px 16px",
                    minHeight: "26px",
                  }}
                >
                  {/* Service name */}
                  <div style={{ flex: 1, minWidth: 0, paddingLeft: "4px" }}>
                    <span
                      style={{
                        ...ffStyle(service.name),
                        fontSize: "0.8125rem",
                        fontWeight: 400,
                        color: "#000000",
                        lineHeight: 1.4,
                        display: "block",
                      }}
                    >
                      {service.name}
                    </span>
                    {(service.description || service.note) && (
                      <span
                        style={{
                          ...ffStyle(service.description || service.note || ""),
                          fontSize: "0.625rem",
                          color: "#000000",
                          opacity: 0.6,
                          lineHeight: 1.2,
                          display: "block",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {service.description}
                        {service.description && service.note && <br />}
                        {service.note}
                      </span>
                    )}
                  </div>

                  {/* Values */}
                  {isLongText ? (
                    <span
                      style={{
                        width: "126px", /* 55+6+65 */
                        ...ffStyle(service.baht || ""),
                        fontSize: "0.6875rem",
                        color: "#000000",
                        textAlign: "right",
                        lineHeight: 1.3,
                        flexShrink: 0,
                      }}
                    >
                      {service.baht}
                    </span>
                  ) : hasValues ? (
                    <>
                      <span
                        style={{
                          width: "55px",
                          ...ffStyle(service.credit || ""),
                          fontSize: "0.875rem",
                          fontWeight: 400,
                          color: "#000000",
                          textAlign: "center",
                          flexShrink: 0,
                        }}
                      >
                        {service.credit || ""}
                      </span>
                      <span style={{ width: "6px", flexShrink: 0 }} />
                      <span
                        style={{
                          width: "65px",
                          ...ffStyle(service.baht || ""),
                          fontSize: "0.875rem",
                          fontWeight: 400,
                          color: "#000000",
                          textAlign: "center",
                          flexShrink: 0,
                        }}
                      >
                        {service.baht || ""}
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        style={{
                          width: "55px",
                          display: "flex",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {checkSvg}
                      </span>
                      <span style={{ width: "6px", flexShrink: 0 }} />
                      <span
                        style={{
                          width: "65px",
                          display: "flex",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {checkSvg}
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Note */}
      {note && (
        <div style={{ padding: "0 16px 12px" }}>
          <p
            style={{
              ...ffStyle(note),
              fontSize: "0.6875rem",
              color: textColor,
              opacity: 0.5,
              textAlign: "left",
              margin: 0,
              wordWrap: "break-word",
            }}
          >
            {note}
          </p>
        </div>
      )}
    </div>
  );
};
